import { createFileRoute } from "@tanstack/solid-router";
import { createEffect, createSignal, ErrorBoundary, Suspense } from "solid-js";

import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { ErrorCard } from "~/components/ui/ErrorCard";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import {
  useProfileQuery,
  useUpdateProfileMutation,
} from "~/lib/queries/identity";
import { isThemePreference, themeOptions, useTheme } from "~/lib/theme";

export const Route = createFileRoute("/_authenticated/account")({
  ssr: false,
  component: AccountPage,
});

const inputClass =
  "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:bg-muted/40 disabled:text-muted-foreground";
const helperClass = "text-xs text-muted-foreground mt-2";

function AccountContent() {
  const profileQuery = useProfileQuery();
  const updateProfile = useUpdateProfileMutation();
  const { theme, setTheme } = useTheme();

  const [firstName, setFirstName] = createSignal("");
  const [lastName, setLastName] = createSignal("");
  const [successMessage, setSuccessMessage] = createSignal("");
  const [isUpdatingTheme, setIsUpdatingTheme] = createSignal(false);

  createEffect(() => {
    const profile = profileQuery.data;
    if (!profile) {
      return;
    }

    setFirstName(profile.firstName);
    setLastName(profile.lastName);
    // Sync theme from server if it's a valid theme preference
    if (
      profile.theme &&
      isThemePreference(profile.theme) &&
      profile.theme !== theme()
    ) {
      setTheme(profile.theme);
    }
  });

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    setSuccessMessage("");

    await updateProfile.mutateAsync({
      firstName: firstName().trim(),
      lastName: lastName().trim(),
      theme: theme(),
    });

    setSuccessMessage("Profile updated.");
  };

  const handleThemeChange = async (newTheme: string) => {
    setTheme(newTheme as "light" | "dark" | "system");
    setIsUpdatingTheme(true);
    try {
      await updateProfile.mutateAsync({
        firstName: firstName(),
        lastName: lastName(),
        theme: newTheme,
      });
    } catch (error) {
      // If theme update fails, revert the local theme
      const serverTheme = profileQuery.data?.theme;
      if (serverTheme && isThemePreference(serverTheme)) {
        setTheme(serverTheme);
      } else {
        setTheme("system");
      }
      console.error("Failed to update theme:", error);
    } finally {
      setIsUpdatingTheme(false);
    }
  };

  return (
    <div class="max-w-4xl mx-auto space-y-6">
      <div class="space-y-1">
        <h1 class="text-xl font-semibold text-foreground">Account settings</h1>
        <p class="text-sm text-muted-foreground">
          Manage your profile details and visual preferences.
        </p>
      </div>

      {profileQuery.isPending && (
        <div class="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)]">
          <Card>
            <CardHeader>
              <Skeleton class="h-6 w-40 rounded-md" />
              <Skeleton class="h-4 w-72 rounded-md" />
            </CardHeader>
            <CardContent class="space-y-4">
              <Skeleton class="h-10 w-full rounded-lg" />
              <Skeleton class="h-10 w-full rounded-lg" />
              <Skeleton class="h-10 w-full rounded-lg" />
              <Skeleton class="h-10 w-full rounded-lg" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton class="h-6 w-32 rounded-md" />
              <Skeleton class="h-4 w-48 rounded-md" />
            </CardHeader>
            <CardContent class="space-y-3">
              <Skeleton class="h-10 w-full rounded-lg" />
              <Skeleton class="h-10 w-full rounded-lg" />
              <Skeleton class="h-10 w-full rounded-lg" />
            </CardContent>
          </Card>
        </div>
      )}

      {profileQuery.isError && (
        <ErrorCard message="Failed to load your account settings. Please try again." />
      )}

      {profileQuery.isSuccess && profileQuery.data && (
        <div class="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>
                Your email and username are managed by authentication and can’t
                be edited here.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form class="space-y-5" onSubmit={handleSubmit}>
                <div class="grid gap-5 sm:grid-cols-2">
                  <div class="space-y-1.5">
                    <Label for="first-name">First Name</Label>
                    <input
                      id="first-name"
                      type="text"
                      class={inputClass}
                      value={firstName()}
                      onInput={(event) => {
                        setFirstName(event.currentTarget.value);
                        setSuccessMessage("");
                      }}
                    />
                  </div>
                  <div class="space-y-1.5">
                    <Label for="last-name">Last Name</Label>
                    <input
                      id="last-name"
                      type="text"
                      class={inputClass}
                      value={lastName()}
                      onInput={(event) => {
                        setLastName(event.currentTarget.value);
                        setSuccessMessage("");
                      }}
                    />
                  </div>
                </div>

                <div class="grid gap-5 sm:grid-cols-2">
                  <div class="space-y-1.5">
                    <Label for="email">Email</Label>
                    <input
                      id="email"
                      type="email"
                      class={inputClass}
                      value={profileQuery.data.email}
                      disabled
                    />
                    <p class={helperClass}>Email is read-only.</p>
                  </div>
                  <div class="space-y-1.5">
                    <Label for="username">Username</Label>
                    <input
                      id="username"
                      type="text"
                      class={inputClass}
                      value={profileQuery.data.username}
                      disabled
                    />
                    <p class={helperClass}>Username is read-only.</p>
                  </div>
                </div>

                <div class="flex items-center justify-between gap-4 pt-2">
                  <div class="space-y-1">
                    {successMessage() && (
                      <p class="text-sm text-primary">{successMessage()}</p>
                    )}
                    {updateProfile.isError && (
                      <p class="text-sm text-destructive">
                        {updateProfile.error instanceof Error
                          ? updateProfile.error.message
                          : "We couldn’t update your profile. Please try again."}
                      </p>
                    )}
                  </div>
                  <Button type="submit" disabled={updateProfile.isPending}>
                    {updateProfile.isPending ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>
                Choose how Reliquo looks on this device.
              </CardDescription>
            </CardHeader>
            <CardContent class="space-y-3">
              {themeOptions.map((option) => (
                <button
                  type="button"
                  onClick={() => handleThemeChange(option.value)}
                  disabled={isUpdatingTheme()}
                  class={`w-full rounded-xl border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed ${
                    theme() === option.value
                      ? "border-primary bg-primary/8 text-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground"
                  }`}
                >
                  <div class="flex items-center justify-between gap-3">
                    <div>
                      <p class="text-sm font-medium text-inherit">
                        {option.label}
                      </p>
                      <p class="text-xs text-muted-foreground mt-1">
                        {option.description}
                      </p>
                    </div>
                    <div
                      class={`size-2.5 rounded-full ${
                        theme() === option.value ? "bg-primary" : "bg-muted"
                      }`}
                    />
                  </div>
                </button>
              ))}
              <p class={helperClass}>
                Theme preference is stored in this browser.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function AccountPage() {
  return (
    <ErrorBoundary
      fallback={(err) => (
        <ErrorCard
          title="Account settings error"
          message={err?.message ?? "An unexpected error occurred."}
        />
      )}
    >
      <Suspense
        fallback={
          <div class="max-w-4xl mx-auto">
            <Skeleton class="h-48 w-full rounded-xl" />
          </div>
        }
      >
        <AccountContent />
      </Suspense>
    </ErrorBoundary>
  );
}
